import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router';
import { CustomerService } from '../../../services/customer.service';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ListFinancialHistoryComponent } from '../list-financial-history/list-financial-history.component';
import { TreeTableModule } from 'primeng/treetable';
import { TagModule } from 'primeng/tag';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { DropdownModule } from 'primeng/dropdown';
import { PaginatorModule } from 'primeng/paginator';
import { InputTextModule } from 'primeng/inputtext';
import { DateRangeEnum } from '../../../models/enums/dateRange.enum';
import { CustomerRevenueSummary } from '../../../models/view-models/customerRevenueSummary.viewmodel';
import { State } from '../../../models/state.model';
import { City } from '../../../models/city.model';
import { StateService } from '../../../services/state.service';
import { CityService } from '../../../services/city.service';

@Component({
  selector: 'app-list-customer',
  templateUrl: './list-customer.component.html',
  styleUrls: ['./list-customer.component.css'],
  imports: [
    CommonModule,
    TableModule,
    ButtonModule,
    ConfirmDialogModule,
    ToastModule,
    FormsModule,
    DialogModule,
    ListFinancialHistoryComponent,
    TreeTableModule,
    TagModule,
    DropdownModule,
    PaginatorModule,
    InputTextModule
  ]
})
export class ListCustomerComponent implements OnDestroy {

  /** Status de receita: quitado, pendente, normal, cancelado */
  statusOptions = [
    { label: 'Todos', value: 'todos' },
    { label: 'Pendente', value: 'pendente' },
    { label: 'Normal', value: 'normal' },
    { label: 'Quitado', value: 'quitado' },
    { label: 'Cancelado', value: 'cancelado' }
  ];

  /** Status ativo/inativo do cliente */
  activeStatusOptions = [
    { label: 'Todos', value: undefined },
    { label: 'Ativos', value: true },
    { label: 'Inativos', value: false }
  ];

  private searchSubject = new Subject<string>();
  private searchSubscription?: Subscription;
  private cpfSubject = new Subject<string>();
  private cpfSubscription?: Subscription;
  private idSubject = new Subject<string>();
  private idSubscription?: Subscription;

  showModal: boolean = false;
  customers: CustomerRevenueSummary[] = [];
  selectedCustomerId!: number;
  searchName = '';
  searchCpf = '';
  searchCode = '';
  currentStatus = 'todos';
  selectedActiveStatus = this.activeStatusOptions[0].value;
  states: State[] = [];
  cities: City[] = [];
  selectedState = 'AL';
  selectedCity: string | null = null;
  loading = true;
  private syncingFromQuery = false;

  first: number = 0;
  rows: number = 5;
  totalRecords: number = 0;
  currentPage: number = 1;

  constructor(
    private customerService: CustomerService,
    private stateService: StateService,
    private cityService: CityService,
    public router: Router,
    private route: ActivatedRoute
  ) {
    this.searchSubscription = this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      this.searchName = term;
      this.loadCustomers();
      this.updateQueryParamsFromState();
    });
    this.cpfSubscription = this.cpfSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      this.searchCpf = term;
      this.loadCustomers();
      this.updateQueryParamsFromState();
    });
    this.idSubscription = this.idSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(term => {
      this.searchCode = term;
      this.loadCustomers();
      this.updateQueryParamsFromState();
    });
  }

  ngOnInit(): void {
    this.applyFiltersFromQuery(this.route.snapshot.queryParamMap);
    this.loadCustomers();
    this.loadStates();
  }

  private applyFiltersFromQuery(q: ParamMap): void {
    this.syncingFromQuery = true;
    try {
      const nome = q.get('q');
      if (nome !== null) {
        this.searchName = nome;
      }

      const cpf = q.get('cpf');
      if (cpf !== null) {
        this.searchCpf = cpf;
      }

      const codigo = q.get('codigo');
      if (codigo !== null && codigo !== '') {
        this.searchCode = codigo;
      }

      const status = q.get('status');
      if (status !== null && this.statusOptions.some(o => o.value === status)) {
        this.currentStatus = status;
      }

      const ativo = q.get('ativo');
      if (ativo === 'true') {
        this.selectedActiveStatus = true;
      } else if (ativo === 'false') {
        this.selectedActiveStatus = false;
      }

      const uf = q.get('uf');
      if (uf !== null && uf.length >= 2) {
        this.selectedState = uf;
      }

      const cidade = q.get('cidade');
      if (cidade !== null) {
        this.selectedCity = cidade === '' ? null : cidade;
      }

      const pageStr = q.get('page');
      if (pageStr !== null && pageStr !== '') {
        const pg = parseInt(pageStr, 10);
        if (!isNaN(pg) && pg >= 1) {
          this.currentPage = pg;
        }
      }

      this.first = (this.currentPage - 1) * this.rows;
    } finally {
      this.syncingFromQuery = false;
    }
  }

  private buildQueryParamsFromState(): Params {
    const params: Params = {};

    const q = this.searchName?.trim();
    if (q) {
      params['q'] = q;
    }

    const cpf = this.searchCpf?.trim();
    if (cpf) {
      params['cpf'] = cpf;
    }

    const codigo = this.searchCode?.trim();
    if (codigo) {
      params['codigo'] = codigo;
    }

    if (this.currentStatus !== 'todos') {
      params['status'] = this.currentStatus;
    }

    if (this.selectedActiveStatus !== undefined) {
      params['ativo'] = String(this.selectedActiveStatus);
    }

    if (this.selectedState && this.selectedState !== 'AL') {
      params['uf'] = this.selectedState;
    }

    if (this.selectedCity) {
      params['cidade'] = this.selectedCity;
    }

    if (this.currentPage > 1) {
      params['page'] = String(this.currentPage);
    }

    return params;
  }

  private updateQueryParamsFromState(): void {
    if (this.syncingFromQuery) {
      return;
    }
    const queryParams = this.buildQueryParamsFromState();
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true
    });
  }

  loadStates(): void {
    this.stateService.getAll('', false, null).subscribe({
      next: (data) => {
        this.states = data.data || [];
        this.loadCities();
      }
    });
  }

  loadCities(): void {
    this.cityService.getAll('', null, this.selectedState, false, null).subscribe({
      next: (data) => {
        this.cities = data.data || [];
      }
    });
  }

  onStateChange(): void {
    this.selectedCity = null;
    this.currentPage = 1;
    this.first = 0;
    this.loadCities();
    this.loadCustomers();
    this.updateQueryParamsFromState();
  }

  onCityChange(): void {
    this.currentPage = 1;
    this.first = 0;
    this.loadCustomers();
    this.updateQueryParamsFromState();
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
    this.cpfSubscription?.unsubscribe();
    this.idSubscription?.unsubscribe();
  }

  onRevenueStatusChange(): void {
    this.currentPage = 1;
    this.first = 0;
    this.loadCustomers();
    this.updateQueryParamsFromState();
  }

  onActiveStatusChange(): void {
    this.currentPage = 1;
    this.first = 0;
    this.loadCustomers();
    this.updateQueryParamsFromState();
  }

  onSearchNameChange(): void {
    this.currentPage = 1;
    this.first = 0;
    this.searchSubject.next(this.searchName);
  }

  onSearchCpfChange(): void {
    this.currentPage = 1;
    this.first = 0;
    this.cpfSubject.next(this.searchCpf);
  }

  onSearchCodeChange(): void {
    this.currentPage = 1;
    this.first = 0;
    this.idSubject.next(this.searchCode);
  }

  loadCustomers() {
    this.loading = true;
    const idParam = this.searchCode?.trim() ? (Number(this.searchCode) || null) : null;
    this.customerService.getCustomersRevenueSummary(
      this.searchName,
      this.searchCpf ?? '',
      idParam,
      this.currentStatus,
      DateRangeEnum.All,
      this.selectedCity,
      this.currentPage,
      true,
      this.selectedActiveStatus
    ).subscribe({
      next: (data) => {
        this.customers = data.data;
        this.totalRecords = data.pagination.totalRecords;
        this.loading = false;
      },
      error: () => this.loading = false
    });
  }

  onPageChange(event: any): void {
    this.first = event.first;
    this.currentPage = Math.floor(this.first / this.rows) + 1;
    this.loadCustomers();
    this.updateQueryParamsFromState();
  }

  editCustomer(customer: any) {
    const queryParams = this.buildQueryParamsFromState();
    void this.router
      .navigate([], { relativeTo: this.route, queryParams, replaceUrl: true })
      .finally(() => {
        void this.router.navigate(['/app/customers/edit', customer.customerId]);
      });
  }

  changeModal(id: number){
    this.showModal = !this.showModal;
    this.selectedCustomerId = id;
  }

  createCustomer() {
    const queryParams = this.buildQueryParamsFromState();
    void this.router
      .navigate([], { relativeTo: this.route, queryParams, replaceUrl: true })
      .finally(() => {
        void this.router.navigate(['/app/customers/new']);
      });
  }

  getStatusSeverity(status: string): string {
    switch (status) {
      case 'quitado':
        return 'success';
      case 'pendente':
        return 'warn';
      case 'normal':
        return 'info';
      case 'cancelado':
        return 'danger';
      default:
        return 'info';
    }
  }

  getActiveStatusLabel(active: boolean): string {
    return active ? 'Ativo' : 'Bloqueada';
  }

  getActiveStatusSeverity(active: boolean): string {
    return active ? 'success' : 'danger';
  }

  getCustomerAddress(customer: CustomerRevenueSummary): string {
    return [customer.address, customer.addressNumber, customer.district, customer.city, customer.state]
      .filter(Boolean)
      .join(', ');
  }

}